import { JSX } from "preact";
import { Section } from "./Section.tsx";

/**
 * FeatureSection - A reusable container for feature introduction sections
 * 
 * This component provides a structured layout for introducing features, services, or company information
 * with a title, content area, and visual element. It's designed to be flexible and reusable.
 * 
 * Example usage:
 * ```mdx
 * <FeatureSection>
 *   <FeatureContent>
 *     <FeatureTitle>Who is Gage?</FeatureTitle>
 *     <FeatureDescription>
 *       <FeatureLeadText>
 *         Welcome to <FeatureHighlight>Gage Technologies</FeatureHighlight>, where your success is our passion!
 *       </FeatureLeadText>
 *       <FeaturePoints>
 *         <FeaturePoint icon="👥">
 *           <strong>A family of dedicated technology experts</strong> who bring unmatched vision and commitment
 *         </FeaturePoint>
 *         <FeaturePoint icon="🤝">
 *           <strong>Relationship-driven approach</strong> that builds lasting partnerships
 *         </FeaturePoint>
 *         <FeaturePoint icon="💡">
 *           <strong>Tailored, innovative solutions</strong> that transform how you work
 *         </FeaturePoint>
 *       </FeaturePoints>
 *     </FeatureDescription>
 *     <FeatureValues>
 *       What sets us apart? Our values. We prioritize <strong>trust</strong>, <strong>collaboration</strong>, and <strong>your needs</strong> above all else, ensuring technology serves you, not the other way around.
 *     </FeatureValues>
 *   </FeatureContent>
 *   <FeatureVisual>
 *     <img src="/img/section-images/about-gage.webp" alt="Gage Technologies" />
 *   </FeatureVisual>
 * </FeatureSection>
 * ```
 */

export function FeatureSection(props: { children: JSX.Element }) {
  return (
    <Section data-accent>
      <div class="feature-section-container">
        {props.children}
      </div>
    </Section>
  );
}

export function FeatureTitle(props: { children: JSX.Element }) {
  return <h2 class="feature-title">{props.children}</h2>;
}

export function FeatureContent(props: { children: JSX.Element }) {
  return <div class="feature-content">{props.children}</div>;
}

export function FeatureDescription(props: { children: JSX.Element }) {
  return <div class="feature-description">{props.children}</div>;
}

export function FeatureLeadText(props: { children: JSX.Element }) {
  return <p class="lead-text">{props.children}</p>;
}

export function FeatureHighlight(props: { children: JSX.Element }) {
  return <span class="highlight">{props.children}</span>;
}

export function FeaturePoints(props: { children: JSX.Element }) {
  return <div class="key-points">{props.children}</div>;
}

export function FeaturePoint(props: { icon: string; children: JSX.Element }) {
  return (
    <div class="point">
      <div class="point-icon">
        {props.icon.startsWith("/")
          ? <img src={props.icon} alt="" />
          : props.icon}
      </div>
      <div class="point-text">{props.children}</div>
    </div>
  );
}

export function FeatureValues(props: { children: JSX.Element }) {
  return <p class="values-text">{props.children}</p>;
}

export function FeatureVisual(props: { children: JSX.Element }) {
  return <div class="feature-visual">{props.children}</div>;
}